<template>
  <div
    data-test="subsections-group"
    class="campsite-subsections"
    :class="{ desktop: $isDesktop }"
  >
    <campsite-subsection
      v-if="!$isDesktop"
      name="description"
      :open="scrolledToElement === '#description'"
      @subsection-closed="$emit('subsection-closed')"
    >
      <template #title>
        <translate>Description</translate>
      </template>
      <template #contents>
        <campsite-description :campsite="campsite" />
      </template>
    </campsite-subsection>

    <template v-if="campsite.rateCount && !$isDesktop">
      <a
        v-scrollto="{
          target: '.campsite-subsection.reviews',
          options: { duration: 200, offset: 0 },
        }"
        href="#reviews"
        class="reviews-jump"
        @click.prevent.stop
      >
        <translate tag="h2">Reviews</translate>
        <img
          loading="lazy"
          src="@/assets/images/down-rounded-double.svg"
          width="16"
          height="16"
          alt=""
        />
      </a>
    </template>

    <div
      v-if="campsite.youtube"
      id="video"
      v-observe-visibility="{
        callback: onVideoVisibilityChanged,
        once: true,
      }"
      class="video-placeholder campsite-subsection"
      data-campsite-subsection
    >
      <lazy-campsite-video
        v-if="loadVideo"
        :youtube-video-i-d="campsite.youtube"
      />
    </div>

    <campsite-subsection name="features" :open="$isDesktop">
      <template #title>
        <translate>Features</translate>
      </template>
      <template #contents>
        <lazy-campsite-features
          :facilities-groups="campsite.facilitiesGroups"
          :super-facilities="campsite.superFacilities"
        />
      </template>
    </campsite-subsection>

    <campsite-subsection name="location" :open="$isDesktop">
      <template #title>
        <translate>Location</translate>
      </template>
      <template #contents>
        <lazy-campsite-location :campsite="campsite" />
      </template>
    </campsite-subsection>

    <lazy-campsite-local-attractions-subsection :campsite="campsite" />

    <lazy-campsite-notices-subsection
      v-if="isBookable && !$isDesktop"
      :warnings="campsite.warnings"
      :notices="campsite.notices"
      :notices-needs-translation="campsite.noticesNeedsTranslation"
      :campsite-notices="campsite.campsiteNotices"
    />

    <lazy-campsite-policies
      v-if="isBookable"
      id="policies"
      :campsite="campsite"
      :open="scrolledToElement === '#policies' || hashInUrl === '#policies'"
      class="campsite-subsection"
      data-campsite-subsection
      @subsection-closed="$emit('subsection-closed')"
    />

    <ui-collapse
      v-if="isBookable"
      id="booking-terms"
      data-test="booking-terms-accordion"
      class="campsite-subsection"
      :selected="
        scrolledToElement === '#booking-terms' || hashInUrl === '#booking-terms'
      "
      :collapse-id="`collapse-booking-terms`"
    >
      <template #ui-collapse-title>
        <translate>Booking terms and conditions</translate>
      </template>
      <template #ui-collapse-body>
        <lazy-campsite-terms-and-conditions
          :needs-translation="campsite.termsAndConditionsNeedsTranslation"
          :terms-and-conditions="campsite.termsAndConditions"
        />
      </template>
    </ui-collapse>

    <lazy-payment-terms
      v-if="isBookable"
      id="payment-terms"
      :open="
        scrolledToElement === '#payment-terms' || hashInUrl === '#payment-terms'
      "
      :payment-terms="campsite.paymentTerms"
      :allow-override-immediate="paymentInfo.allowOverrideImmediate"
      :hosted-full-payment="campsite.hostedFullPayment"
      :payment-advance-days="paymentInfo.paymentAdvanceDays"
      class="campsite-subsection"
    />

    <lazy-campsite-offers-subsection
      v-if="showOffers"
      :pitchtypes="pitchtypesWithOffers"
      class="campsite-subsection"
    />
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { CampsiteDetails } from '../../campsite-details/types'
import CampsiteSubsection from './subsections/CampsiteSubsection.vue'

@Component({
  components: {
    CampsiteSubsection,
  },
})
class SubsectionsGroup extends Vue {
  loadVideo = false

  @Prop({ required: true })
  campsite: CampsiteDetails

  @Prop({ required: false })
  scrolledToElement?

  hashInUrl = ''

  @Watch('$route.hash', { immediate: true })
  hashChanged(hash) {
    if (this.$.isUnmounted) return
    this.hashInUrl = hash
  }

  onVideoVisibilityChanged(isVisible) {
    if (this.$.isUnmounted) return
    this.loadVideo = isVisible
  }

  get isBookable() {
    return this.campsite.isBookable
  }

  get showOffers() {
    return this.isBookable && this.hasOffers
  }

  get hasOffers() {
    return this.campsite.pitchtypes.some((p) => p.activeOffers.length > 0)
  }

  get paymentInfo() {
    return this.campsite.paymentInfo
  }

  get pitchtypesWithOffers() {
    return this.campsite.pitchtypes.filter((p) => p.activeOffers.length > 0)
  }
}

export default toNative(SubsectionsGroup)
</script>

<style scoped>
.campsite-subsections :deep(.ui-collapse-content) {
  padding: 1.4em;
  font-size: var(--body);
}

.ui-collapse {
  border-bottom: 1.25em solid var(--colour-page-background);
}

.notices :deep(.campsite-notices) {
  font-size: var(--body);
}

.help-text a {
  color: var(--colour-text-light);
}

.reviews-jump {
  background: white;
  padding: 1em 1.4em;
  border-bottom: 4px solid var(--colour-page-background);
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
}

.campsite-subsections :deep(.ui-collapse-title),
.reviews-jump h2 {
  font-size: 1.6em;
  line-height: 1.15;
  margin: 0;
  font-weight: 400;
  color: var(--colour-dark-blue);
}

.campsite-subsections :deep(.ui-collapse) {
  border-bottom: 4px solid var(--colour-page-background);
}

.reviews-jump img {
  width: 1em;
  margin-right: 0.2em;
}

.video-placeholder {
  aspect-ratio: auto 3/2;
  width: 100vw;
}

/* desktop & tablet */
.campsite-subsections.desktop :deep(.ui-collapse-title) {
  font-size: 2.25em;
  font-weight: normal;
}

.campsite-subsections.desktop :deep(.water-quality-scroller) {
  width: auto;
}

.campsite-subsections.desktop :deep(.water-quality-table) {
  font-size: inherit;
}

.campsite-subsections.desktop :deep(.local-attractions) {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto auto auto;
  column-gap: 4em;
}

.campsite-subsections.desktop :deep(.local-attractions section) {
  font-size: inherit;
  line-height: 1.4;
}

.campsite-subsections.desktop :deep(.local-attractions .nearby-activities) {
  grid-row: 1/2;
  grid-column: 1/3;
}

.campsite-subsections.desktop :deep(.local-attractions .visit-britain) {
  grid-row: 1/2;
  grid-column: 3/4;
}

.campsite-subsections.desktop
  :deep(.local-attractions .campsite-water-quality) {
  grid-row: 2/3;
  grid-column: 1/3;
}

.campsite-subsections.desktop :deep(.local-attractions .national-trust) {
  grid-row: 2/3;
  grid-column: 3/4;
}

.campsite-subsections.desktop :deep(.local-attractions .cycle-routes) {
  grid-row: 3/4;
  grid-column: 3/4;
}

.campsite-subsections.desktop :deep(.local-attractions .activities-list) {
  grid-row: 3/4;
  grid-column: 1/3;
}

.campsite-subsections.desktop
  :deep(.local-attractions.with-festivals .activities-list) {
  grid-row: 3/4;
  grid-column: 1/2;
}

.campsite-subsections.desktop
  :deep(.local-attractions.with-festivals .festivals) {
  grid-row: 3/4;
  grid-column: 2/3;
}

.campsite-subsections.desktop :deep(.local-attractions .distances-note) {
  grid-row: 4/5;
  grid-column: 1/4;
}

.desktop .video-placeholder {
  display: flex;
  width: 100%;
}
</style>
